import { type FC, useContext } from 'react';
import { useFragment, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import CircleCheck from 'dibs-icons/exports/legacy/CircleCheck';
import { ModalBody } from 'dibs-elements/exports/ModalBody';
import { Button } from 'dibs-elements/exports/Button';
import { Link } from 'dibs-elements/exports/Link';
import { etherscanUrl } from 'dibs-crypto-utils/exports/formattingUtils';
import { addParams } from 'dibs-crypto-utils/exports/imageOptimizationParams';
import { NftPdpBidModalContext } from '../NftPdpBidModalContext';

import { type BidSuccessfulContent_nftItem$key } from './__generated__/BidSuccessfulContent_nftItem.graphql';

import styles from './BidSuccessfulContent.scss';

type Props = {
    nftItem: BidSuccessfulContent_nftItem$key;
};

export const BidSuccessfulContent: FC<Props> = ({ nftItem: nftItemRef }) => {
    const { transactionHash, setTransactionHash, setModalOpen } = useContext(NftPdpBidModalContext);
    const nftItem = useFragment(
        graphql`
            fragment BidSuccessfulContent_nftItem on NftItem {
                metadata {
                    ... on NftDigitalArtMetadata {
                        media {
                            webUrl
                            posterFrameUrl
                        }
                    }
                }
            }
        `,
        nftItemRef
    );
    const { metadata } = nftItem || {};
    const { media } = metadata || {};
    const { webUrl, posterFrameUrl } = media || {};
    return (
        <HeadingLevel>
            {Header => (
                <>
                    <div className={styles.posterFrameWrapper}>
                        <img
                            src={
                                addParams(
                                    posterFrameUrl || webUrl,
                                    styles.MEDIA_WIDTH,
                                    styles.MEDIA_HEIGHT
                                ) || ''
                            }
                            className={styles.posterFrameImage}
                            alt=""
                        />
                    </div>
                    <div className={styles.headerWrapper}>
                        <Header className={styles.header}>
                            <CircleCheck className={styles.checkIcon} />
                            <FormattedMessage
                                defaultMessage="Your Bid Was Placed Successfully"
                                id="nft.pdp.bidSuccessModal.header"
                            />
                        </Header>
                    </div>
                    <ModalBody className={styles.successModalBody}>
                        <div className={styles.bodyCopy}>
                            <span>
                                <FormattedMessage
                                    defaultMessage="It was confirmed on the Ethereum network"
                                    id="nft.pdp.bidSuccessModal.bidConfirmed"
                                />
                            </span>
                            <span>
                                <FormattedMessage
                                    defaultMessage="<b>If you are outbid</b>, you will be notified via email and you may place another bid on this item before the auction ends."
                                    id="nft.pdp.bidSuccessModal.outbid"
                                    values={{
                                        b: chunks => <strong>{chunks}</strong>,
                                    }}
                                />
                            </span>
                            <span>
                                <FormattedMessage
                                    defaultMessage="<b>If your bid is the highest at the end</b>, you will become the owner of this item and be notified via email."
                                    id="nft.pdp.bidSuccessModal.highestBid"
                                    values={{
                                        b: chunks => <strong>{chunks}</strong>,
                                    }}
                                />
                            </span>
                        </div>
                        <Button
                            fullWidth
                            onClick={() => {
                                setModalOpen(false);
                                setTransactionHash(null);
                            }}
                        >
                            <FormattedMessage
                                defaultMessage="View Item"
                                id="nft.pdp.bidSuccessModal.viewItem"
                            />
                        </Button>
                        <Link
                            href={etherscanUrl(`/tx/${transactionHash}`)}
                            target="_blank"
                            className={styles.txLink}
                        >
                            <FormattedMessage
                                defaultMessage="View Etherscan transaction"
                                id="nft.pdp.bidSuccessModal.viewTransaction"
                            />
                        </Link>
                    </ModalBody>
                </>
            )}
        </HeadingLevel>
    );
};
